import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import DrawerWrapper, { DrawerFooter } from 'components/IAP/DrawerWrapper';
import FormCheckboxField from 'components/IAP/formFields/FormCheckboxField';
import FormDateTimeField from 'components/IAP/formFields/FormDateTimeField';
import FormSelectField from 'components/IAP/formFields/FormSelectField';
import FormTextField from 'components/IAP/formFields/FormTextField';
import { ACTION_STATUS_OPTIONS } from 'components/IAP/ICSConstants';
import { useId } from 'react';
import { Offcanvas, Row } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { PlannedAction } from 'types/iapBaseTypes';

export type PlannedActionEditDrawerProps = {
  /** True when adding a new row; header shows "Add Action" vs "Edit Action". */
  isNewAction: boolean;
  onDismiss: () => void;
  selectedAction: PlannedAction;
  onSave: (selectedAction: PlannedAction) => void;
};

const PlannedActionEditDrawer: React.FC<PlannedActionEditDrawerProps> = ({
  isNewAction,
  onDismiss,
  selectedAction,
  onSave,
}) => {
  const drawerTitle = isNewAction ? 'Add Action' : 'Edit Action';
  const form = useForm({
    mode: 'onChange',
    defaultValues: selectedAction,
  });

  const { handleSubmit, watch } = form;
  const watchedFields = watch();
  const formDomId = useId();

  const submitPlannedActionEdit = async (formData: PlannedAction) => {
    onSave(formData);
  };

  const saveDisabled = !watchedFields.task.trim();

  return (
    <DrawerWrapper toggle={onDismiss} title={drawerTitle}>
      <FormProvider {...form}>
        <form id={formDomId} onSubmit={handleSubmit(submitPlannedActionEdit)}>
          <Offcanvas.Body>
            <Row>
              <FormTextField
                title="Name"
                fieldName="task"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 250,
                    message: 'This maximum length is 250 characters',
                  },
                }}
                variant="half"
              />
            </Row>
            <Row>
              <FormTextField
                title="Point of Contact"
                fieldName="pointOfContact"
                variant="half"
              />
              <FormCheckboxField
                title="Briefed POC"
                fieldName="pocBriefed"
                variant="half"
              />
            </Row>
            <Row>
              <FormSelectField
                title="Status"
                fieldName="status"
                options={ACTION_STATUS_OPTIONS}
                variant="full"
                rules={{
                  required: { value: true, message: 'Status is required' },
                }}
              />
            </Row>
            <Row>
              <FormDateTimeField
                title="Start Date & Time"
                fieldName="startDate"
                variant="full"
              />
            </Row>
            <Row>
              <FormDateTimeField
                title="Deadline Date & Time"
                fieldName="deadline"
                variant="full"
              />
            </Row>
          </Offcanvas.Body>
        </form>
      </FormProvider>
      <DrawerFooter>
        <StylishNewButton
          className="button--secondary button--reverse"
          type="button"
          onClick={onDismiss}
        >
          Close
        </StylishNewButton>
        <StylishNewButton
          className="button--primary"
          type="submit"
          form={formDomId}
          disabled={saveDisabled}
        >
          Save
        </StylishNewButton>
      </DrawerFooter>
    </DrawerWrapper>
  );
};

export default PlannedActionEditDrawer;
